import type { User as Auth0User } from '@auth0/auth0-react';

import constructEnterpriseDomain from 'utils/construct-enterprise-domain';

/*

# USAGE GUIDE WHEN YOU NEED TO GET TO THE ENTERPRISE LIST aka membership list aka "Switch Enterprises"

Note that the `/enterprises` path is supposed to be used on `app.{brand}…` origins, not {enterprise}.{brand}….
Depends on where you are, choose the right pattern below:

## Same origin → SPA navigation

Use primitives like `navigate()`, `Link`, or `NavLink` together with `ENTERPRISE_LIST_PATH`.
Avoid `goToEnterpriseList()`; it always full-page reloads.

| Call site             | Use                                                 |
| --------------------- | --------------------------------------------------- |
| ExistingSessionPrompt | `navigate(ENTERPRISE_LIST_PATH, { replace: true })` |

Invite links stay on the app host; no host change.

## Cross origin → full URL navigation

Pass `auth0User` from `useAuth0()` so app.{brand} can reconcile with the enterprise session.

| Call site                              | Use                                   |
| -------------------------------------- | ------------------------------------- |
| SiteSwitcher “Switch Enterprises”      | `goToEnterpriseList(auth0User)`       |
| FetchFailureModal “Switch Enterprises” | `goToEnterpriseList(auth0User)`       |
| Account / More menu link               | `getEnterpriseListUrl(auth0User)`     |

`goToEnterpriseList()` sets `window.location.href` so it leaves the enterprise subdomain.

*/

/** Internal query param: Auth0 sub from the enterprise origin. */
export const ENTERPRISE_LIST_SUB_PARAM = '_s';

/** In-app path to the membership list on the current origin. */
export const ENTERPRISE_LIST_PATH = '/enterprises';

/**
 * Full URL to the membership list on the `app.{brand}…` origin (white-label aware).
 * For external links and cross-origin jumps.
 */
export const getEnterpriseListUrl = (auth0User?: Auth0User): string => {
  const url = new URL(`${constructEnterpriseDomain()}${ENTERPRISE_LIST_PATH}`);
  if (auth0User?.sub) {
    url.searchParams.set(ENTERPRISE_LIST_SUB_PARAM, auth0User.sub);
  }
  return url.toString();
};

/** Go to the membership list with a full page load. */
export const goToEnterpriseList = (auth0User?: Auth0User): void => {
  window.location.href = getEnterpriseListUrl(auth0User);
};
